import { useCallback, useState } from "react";
import { ZodSchema } from "zod";
import { FiltersTypes } from "@common/types/filters/general";
import { View } from "@common/types/views/views";

const keepView = <TView extends View>(view: TView): TView => view;

interface UseCloseTemplateTaskViewStateParams<TView extends View> {
    viewConfig: Record<string, unknown> | null;
    onViewConfigChange: (viewConfig: Record<string, unknown>) => void;
    parseViewConfig: (viewConfig: Record<string, unknown> | null) => TView;
    serializeView: (view: TView) => Record<string, unknown>;
    viewSchema: ZodSchema<TView>;
    normalizeView?: (view: TView) => TView;
}

export function useCloseTemplateTaskViewState<TView extends View>({
    viewConfig,
    onViewConfigChange,
    parseViewConfig,
    serializeView,
    viewSchema,
    normalizeView = keepView,
}: UseCloseTemplateTaskViewStateParams<TView>) {
    const [view, setView] = useState<TView>(() =>
        normalizeView(parseViewConfig(viewConfig)),
    );

    const [prevInputs, setPrevInputs] = useState({
        viewConfig,
        parseViewConfig,
        normalizeView,
    });
    if (
        viewConfig !== prevInputs.viewConfig ||
        parseViewConfig !== prevInputs.parseViewConfig ||
        normalizeView !== prevInputs.normalizeView
    ) {
        setPrevInputs({ viewConfig, parseViewConfig, normalizeView });
        setView(normalizeView(parseViewConfig(viewConfig)));
    }

    const commit = useCallback(
        (next: TView) => {
            setView(next);
            onViewConfigChange(serializeView(next));
        },
        [onViewConfigChange, serializeView],
    );

    const handleViewChange = useCallback(
        (updatedView: View) => {
            const parsed = viewSchema.safeParse({
                ...view,
                columns: updatedView.columns,
                filters: { ...view.filters, ...updatedView.filters },
                options: updatedView.options ?? view.options,
            });
            if (parsed.success) {
                commit(normalizeView(parsed.data));
            }
        },
        [commit, normalizeView, view, viewSchema],
    );

    const handleFiltersChange = useCallback(
        (patch: Partial<FiltersTypes>) =>
            commit({ ...view, filters: { ...view.filters, ...patch } }),
        [commit, view],
    );

    return { view, handleViewChange, handleFiltersChange };
}
